import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Plus, Search, Building2, Pencil, Trash2, Upload, User, Briefcase, Users, Mail, TrendingUp, ExternalLink } from 'lucide-react';
import { useAuth } from '@core/auth/useAuth';
import { usePermissions } from '@core/auth/usePermissions';
import { clientService, type ClientSortColumn } from '@core/supabase/services/clientService';
import { jobService } from '@core/supabase/services/jobService';
import { applicationService } from '@core/supabase/services/applicationService';
import { maskingService } from '@core/supabase/services/maskingService';
import { Client } from '@core/types';
import { PageHeader } from '@shared/components/ui/PageHeader';
import { SyncStatusBadge } from '@shared/components/SyncStatusBadge';
import { useToast } from '@shared/hooks/useToast';
import { ListPreviewDrawer } from '@shared/components/ui/ListPreviewDrawer';
import { RecordHeader } from '@shared/components/ui/RecordHeader';
import { RecordMetaCard } from '@shared/components/ui/RecordMetaCard';
import { ListConfigureButton } from '@shared/components/ui/ListConfigureButton';
import { ListOperationsDropdown } from '@shared/components/ui/ListOperationsDropdown';
import { objectHistoryUrl } from '@shared/lib/objectHistoryUrl';
import { ConfigureListColumnsModal, colClass } from '@shared/components/ui/ConfigureListColumnsModal';
import { useListColumnVisibility } from '@shared/hooks/useListColumnVisibility';
import type { ListColumnCatalogItem } from '@shared/lib/listColumnPreferences';
import { listExportNotImplemented } from '@shared/lib/listExportStub';
import { downloadClientsCsv } from './clientsListCsv';
import { formatCreatedWhen } from '@shared/lib/formatCreatedWhen';
import { formatDateTimeGmt } from '@shared/lib/formatDateTimeGmt';
import { EmptyState } from '@shared/components/ui/EmptyState';
import { FullPageSpinner } from '@shared/components/ui/Spinner';
import { TablePagination } from '@shared/components/ui/TablePagination';
import { DataTableToolbar } from '@shared/components/ui/DataTableToolbar';
import { DataTableDeleteConfirmModal } from '@shared/components/ui/DataTableDeleteConfirmModal';
import { SortableTh, EnrichColumnTh } from '@shared/components/ui/SortableTableHeader';
import { TableAvatar } from '@shared/components/ui/TableAvatar';
import { RowHoverActions } from '@shared/components/ui/RowHoverActions';
import { GenericManageRightsModal } from '@shared/components/list/GenericManageRightsModal';

function clientPreviewInitials(name: string) {
  const p = name.trim().split(/\s+/).filter(Boolean);
  if (p.length === 0) return '?';
  if (p.length === 1) return p[0]!.slice(0, 2).toUpperCase();
  return (p[0]![0]! + p[p.length - 1]![0]!).toUpperCase();
}

const CLIENTS_LIST_COLUMNS: ListColumnCatalogItem[] = [
  { id: 'contact', label: 'Contact' },
  { id: 'industry', label: 'Industry', optional: true },
  { id: 'jobs', label: 'Jobs (#)' },
  { id: 'apps', label: 'Applications (#)' },
  { id: 'masking', label: 'Masking template' },
  { id: 'created', label: 'Created' },
];

export function ClientsPage() {
  const { user } = useAuth();
  const perms = usePermissions();
  const { toast, showToast } = useToast();
  const navigate = useNavigate();
  const [clients, setClients] = useState<Client[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const headerCheckRef = useRef<HTMLInputElement>(null);
  const [totalItems, setTotalItems] = useState(0);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);
  const [loading, setLoading] = useState(true);
  const [clientStats, setClientStats] = useState<Record<string, { jobs: number; applications: number }>>({});
  const [maskingNames, setMaskingNames] = useState<Record<string, string>>({});
  const [configureOpen, setConfigureOpen] = useState(false);
  const [manageRightsOpen, setManageRightsOpen] = useState(false);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [sortBy, setSortBy] = useState<ClientSortColumn>('created_at');
  const [sortAsc, setSortAsc] = useState(false);
  const [previewClientId, setPreviewClientId] = useState<string | null>(null);
  const [clientRowMenu, setClientRowMenu] = useState<{ x: number; y: number; clientId: string } | null>(null);
  const clientRowMenuRef = useRef<HTMLDivElement>(null);

  const clientsCatalog = useMemo(() => CLIENTS_LIST_COLUMNS, []);
  const { show, setColumn, reset, visible } = useListColumnVisibility(
    'clients',
    user?.orgId ?? null,
    clientsCatalog,
  );

  const loadOrgStats = useCallback(() => {
    if (!user?.orgId) return;
    Promise.all([
      jobService.list(user.orgId),
      applicationService.list(user.orgId),
      maskingService.listTemplates(user.orgId),
    ]).then(([jobs, apps, templates]) => {
      const jList = Array.isArray(jobs) ? jobs : (jobs as { data: { client_id: string }[] }).data;
      const aList = apps;
      const jobsByClient: Record<string, number> = {};
      const appsByClient: Record<string, number> = {};
      jList.forEach((j) => {
        const cid = (j as { client_id: string }).client_id;
        if (cid) jobsByClient[cid] = (jobsByClient[cid] ?? 0) + 1;
      });
      aList.forEach((a) => {
        const cid = (a as { client_id: string }).client_id;
        if (cid) appsByClient[cid] = (appsByClient[cid] ?? 0) + 1;
      });
      setClientStats(
        [...new Set([...Object.keys(jobsByClient), ...Object.keys(appsByClient)])].reduce((acc, cid) => {
          acc[cid] = { jobs: jobsByClient[cid] ?? 0, applications: appsByClient[cid] ?? 0 };
          return acc;
        }, {} as Record<string, { jobs: number; applications: number }>)
      );
      setMaskingNames(templates.reduce((acc, t) => { acc[t.id] = t.name; return acc; }, {} as Record<string, string>));
    });
  }, [user?.orgId]);

  useEffect(() => {
    loadOrgStats();
  }, [loadOrgStats]);

  useEffect(() => {
    if (!clientRowMenu) return;
    const close = (e: MouseEvent) => {
      const t = e.target as Node;
      if (clientRowMenuRef.current?.contains(t)) return;
      setClientRowMenu(null);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [clientRowMenu]);

  const runClientsExport = useCallback(
    async (scope: 'selection' | 'all') => {
      if (!user?.orgId) return;
      try {
        const result = await clientService.list(user.orgId, {
          search: search || undefined,
          page: 1,
          pageSize: 10000,
          sortBy,
          sortAsc,
        });
        const r = result as { data: Client[]; total: number };
        let rows = r.data;
        if (scope === 'selection') {
          if (selectedIds.size === 0) {
            showToast({ title: 'Select one or more rows to export, or use CSV — all under Operations.', kind: 'error' });
            return;
          }
          const sel = selectedIds;
          rows = rows.filter((c) => sel.has(c.id));
        }
        if (rows.length === 0) {
          showToast({ title: 'Nothing to export for the current filters.', kind: 'error' });
          return;
        }
        downloadClientsCsv(rows, clientStats, maskingNames);
      } catch (e) {
        showToast({ title: e instanceof Error ? e.message : 'Export failed.', kind: 'error' });
      }
    },
    [user?.orgId, search, sortBy, sortAsc, selectedIds, clientStats, maskingNames],
  );

  useEffect(() => {
    if (!user?.orgId) return;
    setLoading(true);
    clientService
      .list(user.orgId, { search: search || undefined, page, pageSize, sortBy, sortAsc })
      .then((result) => {
        const r = result as { data: Client[]; total: number };
        setClients(r.data);
        setTotalItems(r.total);
      })
      .finally(() => setLoading(false));
  }, [user?.orgId, search, page, pageSize, sortBy, sortAsc]);

  const handleClientSort = (col: ClientSortColumn) => {
    if (sortBy === col) {
      setSortAsc((a) => !a);
    } else {
      setSortBy(col);
      setSortAsc(col === 'created_at' ? false : true);
    }
    setPage(1);
  };

  useEffect(() => {
    if (headerCheckRef.current) {
      headerCheckRef.current.indeterminate = clients.length > 0 && selectedIds.size > 0 && selectedIds.size < clients.length;
    }
  }, [clients.length, selectedIds.size]);

  const handleDeleteClients = async (ids: string[]) => {
    if (!user?.orgId) return;
    if (!perms.canManageClients) {
      showToast({ title: 'You do not have permission to delete clients.', kind: 'error' });
      return;
    }
    if (ids.length === 0) return;
    try {
      for (const id of ids) {
        await clientService.delete(id);
      }
      setSelectedIds(new Set());
      const result = await clientService.list(user.orgId, {
        search: search || undefined,
        page,
        pageSize,
        sortBy,
        sortAsc,
      });
      const r = result as { data: Client[]; total: number };
      setClients(r.data);
      setTotalItems(r.total);
      loadOrgStats();
    } catch (e) {
      showToast({ title: e instanceof Error ? e.message : 'Delete failed.', kind: 'error' });
    }
  };

  return (
    <div>
      {toast}
      <SyncStatusBadge className="mb-3" />
      <PageHeader
        variant="minimal"
        breadcrumb={[{ label: 'Data', href: '/clients' }, { label: 'Clients' }]}
        breadcrumbRowEnd={
          <ListOperationsDropdown
            objectHistoryHref={objectHistoryUrl('clients')}
            onExport={(fmt, scope) => {
              if (fmt === 'csv') void runClientsExport(scope);
              else listExportNotImplemented('Clients', fmt, scope);
            }}
            extraActions={[
              {
                id: 'import',
                label: 'Import',
                icon: <Upload className="vscode-text-secondary" strokeWidth={2} />,
                onClick: () =>
                  showToast({ title: 'Bulk client import is not available. Add clients with New client or the onboarding wizard.', kind: 'error' }),
              },
              {
                id: 'rights',
                label: 'Manage Rights',
                icon: <User className="vscode-text-secondary" strokeWidth={2} />,
                onClick: () => setManageRightsOpen(true),
              },
            ]}
          />
        }
      />

        <div className="relative mb-6">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 vscode-text-tertiary" />
        <input 
          className="w-full h-11 pl-11 pr-4 rounded-xl border vscode-border vscode-bg-secondary text-sm vscode-text-primary placeholder:vscode-text-tertiary focus:outline-none focus:ring-2 focus:ring-blue-500/30 transition-all" 
          placeholder="Search clients by name or industry..." 
          value={search} 
          onChange={(e) => setSearch(e.target.value)} 
        />
      </div>

      {loading ? (
        <FullPageSpinner />
      ) : clients.length === 0 ? (
        <EmptyState
          icon={Building2}
          title="No clients yet"
          description="Add your first client company to start creating jobs and applications."
          action={<Link to="/clients/new" className="btn-primary"><Plus className="w-4 h-4" /> Add Client</Link>}
        />
      ) : (
        <>
        {/* Summary Stats Row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="rounded-xl border vscode-border vscode-bg-secondary p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: 'rgba(59, 130, 246, 0.15)' }}>
                <Building2 className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <p className="text-2xl font-bold vscode-text-primary">{totalItems}</p>
                <p className="text-xs vscode-text-tertiary">Total Clients</p>
              </div>
            </div>
          </div>
          <div className="rounded-xl border vscode-border vscode-bg-secondary p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: 'rgba(16, 185, 129, 0.15)' }}>
                <Briefcase className="w-5 h-5 text-emerald-400" />
              </div>
              <div>
                <p className="text-2xl font-bold vscode-text-primary">{Object.values(clientStats).reduce((a, b) => a + b.jobs, 0)}</p>
                <p className="text-xs vscode-text-tertiary">Total Jobs</p>
              </div>
            </div>
          </div>
          <div className="rounded-xl border vscode-border vscode-bg-secondary p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: 'rgba(139, 92, 246, 0.15)' }}>
                <Users className="w-5 h-5 text-purple-400" />
              </div>
              <div>
                <p className="text-2xl font-bold vscode-text-primary">{Object.values(clientStats).reduce((a, b) => a + b.applications, 0)}</p>
                <p className="text-xs vscode-text-tertiary">Total Applications</p>
              </div>
            </div>
          </div>
          <div className="rounded-xl border vscode-border vscode-bg-secondary p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: 'rgba(245, 158, 11, 0.15)' }}>
                <TrendingUp className="w-5 h-5 text-amber-400" />
              </div>
              <div>
                <p className="text-2xl font-bold vscode-text-primary">{clients.filter(c => (clientStats[c.id]?.jobs ?? 0) > 0).length}</p>
                <p className="text-xs vscode-text-tertiary">Active Clients</p>
              </div>
            </div>
          </div>
        </div>

        <div className="rounded-xl border vscode-border overflow-hidden">
          <DataTableToolbar
            hasTable={true}
            newLabel="New client"
            newHref="/clients/new"
            toolbarEnd={
              <ListConfigureButton onClick={() => setConfigureOpen(true)} title="Table layout for clients" />
            }
          />
          <DataTableDeleteConfirmModal
            open={bulkDeleteOpen}
            onClose={() => setBulkDeleteOpen(false)}
            entityLabel="clients"
            count={selectedIds.size}
            bodyExtra={
              'Deleting a client removes the whole subtree in one step: all jobs, applications, requisitions, contacts, notes, compliance documents, and other records that reference this client are removed together (database ON DELETE CASCADE). You cannot delete only the client while keeping its jobs.'
            }
            onConfirm={() => void handleDeleteClients(Array.from(selectedIds)).then(() => setBulkDeleteOpen(false))}
            requireTypedConfirm="DELETE"
          />
          {selectedIds.size > 0 && (
            <div className="ggj-data-bulk-bar flex flex-wrap items-center gap-2 border-b border-[color-mix(in_srgb,var(--vscode-border-primary)_50%,transparent)] px-4 py-2">
              <span className="text-sm font-medium vscode-text-primary">{selectedIds.size} selected</span>
              <button
                type="button"
                className="btn-secondary inline-flex items-center gap-1 text-xs"
                disabled={selectedIds.size !== 1}
                onClick={() => {
                  const id = Array.from(selectedIds)[0];
                  if (id) navigate(`/clients/${id}`);
                }}
              >
                <Pencil className="h-3.5 w-3.5" /> Edit
              </button>
              <button
                type="button"
                className="btn-secondary inline-flex items-center gap-1 text-xs"
                disabled={!perms.canManageClients}
                onClick={() => setBulkDeleteOpen(true)}
              >
                <Trash2 className="h-3.5 w-3.5" /> Delete
              </button>
            </div>
          )}
          <div className="overflow-x-auto min-w-0">
          <table className="ggj-data-table min-w-[640px]">
            <thead>
              <tr className="text-left">
                <th className="px-4 py-3 w-10">
                  <input
                    type="checkbox"
                    ref={headerCheckRef}
                    checked={clients.length > 0 && selectedIds.size === clients.length}
                    onChange={() => {
                      if (selectedIds.size === clients.length) setSelectedIds(new Set());
                      else setSelectedIds(new Set(clients.map((c) => c.id)));
                    }}
                    className="rounded"
                  />
                </th>
                <SortableTh
                  label="Client"
                  column="name"
                  current={sortBy}
                  asc={sortAsc}
                  onSort={handleClientSort}
                />
                <SortableTh
                  label="Contact"
                  column="contact_email"
                  current={sortBy}
                  asc={sortAsc}
                  onSort={handleClientSort}
                  className={colClass(show('contact'), '')}
                />
                <SortableTh
                  label="Industry"
                  column="industry"
                  current={sortBy}
                  asc={sortAsc}
                  onSort={handleClientSort}
                  className={colClass(show('industry'), '')}
                />
                <th className={colClass(show('jobs'), 'px-4 py-3 w-16')}>Jobs</th>
                <th className={colClass(show('apps'), 'px-4 py-3 w-20')}>Apps</th>
                <th className={colClass(show('masking'), 'px-4 py-3')}>Masking</th>
                <SortableTh
                  label="Created"
                  column="created_at"
                  current={sortBy}
                  asc={sortAsc}
                  onSort={handleClientSort}
                  className={colClass(show('created'), 'whitespace-nowrap')}
                />
                <EnrichColumnTh />
              </tr>
            </thead>
            <tbody>
              {clients.map((c) => {
                const stats = clientStats[c.id];
                return (
                <tr
                  key={c.id}
                  tabIndex={0}
                  className={[
                    selectedIds.has(c.id) ? 'bg-brand-500/10' : '',
                    'cursor-pointer outline-none transition-colors hover:bg-[color-mix(in_srgb,var(--vscode-bg-hover)_65%,transparent)] focus-visible:ring-2 focus-visible:ring-brand-500/40',
                  ].join(' ')}
                  onClick={() => setPreviewClientId(c.id)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setPreviewClientId(c.id);
                    }
                    if (e.key === 'ArrowRight') {
                      e.preventDefault();
                      setPreviewClientId(c.id);
                    }
                    if (e.key === 'ArrowLeft' && previewClientId) {
                      e.preventDefault();
                      setPreviewClientId(null);
                    }
                  }}
                  onContextMenu={(ev) => {
                    ev.preventDefault();
                    setClientRowMenu({ x: ev.clientX, y: ev.clientY, clientId: c.id });
                  }}
                >
                  <td className="px-4 py-3 w-10" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={selectedIds.has(c.id)}
                      onChange={() => {
                        const next = new Set(selectedIds);
                        if (next.has(c.id)) next.delete(c.id);
                        else next.add(c.id);
                        setSelectedIds(next);
                      }}
                      className="rounded border-[var(--vscode-border-primary)]"
                    />
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-lg flex items-center justify-center text-xs font-semibold" style={{ background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)', color: 'white' }}>
                        {clientPreviewInitials(c.name)}
                      </div>
                      <div className="flex flex-col">
                        <p className="text-sm font-medium vscode-text-primary">{c.name}</p>
                        {c.industry?.trim() && (
                          <p className="text-xs vscode-text-tertiary">{c.industry}</p>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className={colClass(show('contact'), 'px-4 py-3')}>
                    {c.contact_email ? (
                      <div className="flex items-center gap-2">
                        <Mail className="w-3.5 h-3.5 vscode-text-tertiary" />
                        <span className="text-sm vscode-text-secondary truncate max-w-[180px] block">{c.contact_email}</span>
                      </div>
                    ) : (
                      <span className="text-sm vscode-text-tertiary">—</span>
                    )}
                  </td>
                  <td className={colClass(show('industry'), 'px-4 py-3 text-sm vscode-text-secondary')}>
                    {c.industry?.trim() ? (
                      <span className="px-2 py-0.5 rounded-full text-xs vscode-bg-tertiary">{c.industry}</span>
                    ) : (
                      <span className="vscode-text-tertiary">—</span>
                    )}
                  </td>
                  <td className={colClass(show('jobs'), 'px-4 py-3')}>
                    {(stats?.jobs ?? 0) > 0 ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium" style={{ background: 'rgba(16, 185, 129, 0.15)', color: 'rgb(52, 211, 153)' }}>
                        <Briefcase className="w-3 h-3" />
                        {stats?.jobs}
                      </span>
                    ) : (
                      <span className="text-sm vscode-text-tertiary">—</span>
                    )}
                  </td>
                  <td className={colClass(show('apps'), 'px-4 py-3')}>
                    {(stats?.applications ?? 0) > 0 ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium" style={{ background: 'rgba(139, 92, 246, 0.15)', color: 'rgb(167, 139, 250)' }}>
                        <Users className="w-3 h-3" />
                        {stats?.applications}
                      </span>
                    ) : (
                      <span className="text-sm vscode-text-tertiary">—</span>
                    )}
                  </td>
                  <td className={colClass(show('masking'), 'px-4 py-3')}>
                    <span className="px-2 py-0.5 rounded text-xs vscode-bg-tertiary vscode-text-secondary">
                      {c.default_masking_template_id ? maskingNames[c.default_masking_template_id] ?? '—' : 'Default'}
                    </span>
                  </td>
                  <td className={colClass(show('created'), 'px-4 py-3 text-sm vscode-text-tertiary tabular-nums whitespace-nowrap')}>
                    {formatCreatedWhen(c.created_at)}
                  </td>
                  <RowHoverActions />
                </tr>
              );})}
            </tbody>
          </table>
          </div>
          <TablePagination
            page={page}
            pageSize={pageSize}
            totalItems={totalItems}
            entityLabel="clients"
            onPageChange={setPage}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setPage(1);
            }}
          />
        </div>
        </>
      )}
      <ConfigureListColumnsModal
        open={configureOpen}
        onClose={() => setConfigureOpen(false)}
        entityLabel="Clients"
        columns={CLIENTS_LIST_COLUMNS}
        value={visible}
        onChange={setColumn}
        onReset={reset}
      />
      <GenericManageRightsModal
        open={manageRightsOpen}
        onClose={() => setManageRightsOpen(false)}
        orgId={user?.orgId ?? null}
        entityKey="clients"
        subtitle="Column visibility uses Configure on the toolbar; per-user access for this client list is stored locally per browser."
      />

      {previewClientId ? (
        (() => {
          const c = clients.find((x) => x.id === previewClientId);
          if (!c) return null;
          const stats = clientStats[c.id];
          return (
            <ListPreviewDrawer
              open
              onClose={() => setPreviewClientId(null)}
              typeLabel="Client"
              title={c.name}
              subtitle={c.industry?.trim() || c.contact_email || undefined}
              openFullHref={`/clients/${c.id}`}
            >
              <div className="space-y-4 px-2">
                {/* Client Header Card */}
                <div className="rounded-xl border vscode-border vscode-bg-secondary p-4">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 rounded-xl flex items-center justify-center text-lg font-bold" style={{ background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)', color: 'white' }}>
                      {clientPreviewInitials(c.name)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-lg font-semibold vscode-text-primary truncate">{c.name}</h3>
                      {c.industry?.trim() && (
                        <p className="text-sm vscode-text-secondary">{c.industry}</p>
                      )}
                      {c.domain && (
                        <a href={`https://${c.domain}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-blue-400 hover:text-blue-300 mt-1">
                          <ExternalLink className="w-3 h-3" />
                          {c.domain}
                        </a>
                      )}
                    </div>
                  </div>
                </div>

                {/* Stats Cards */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="rounded-lg border vscode-border vscode-bg-tertiary p-3 text-center">
                    <div className="flex items-center justify-center gap-2 mb-1">
                      <Briefcase className="w-4 h-4 text-emerald-400" />
                      <span className="text-2xl font-bold text-emerald-400">{stats?.jobs ?? 0}</span>
                    </div>
                    <p className="text-xs vscode-text-tertiary">Jobs</p>
                  </div>
                  <div className="rounded-lg border vscode-border vscode-bg-tertiary p-3 text-center">
                    <div className="flex items-center justify-center gap-2 mb-1">
                      <Users className="w-4 h-4 text-purple-400" />
                      <span className="text-2xl font-bold text-purple-400">{stats?.applications ?? 0}</span>
                    </div>
                    <p className="text-xs vscode-text-tertiary">Applications</p>
                  </div>
                </div>

                {/* Contact Info */}
                {c.contact_email && (
                  <div className="rounded-xl border vscode-border vscode-bg-secondary p-4">
                    <h4 className="text-xs font-semibold uppercase tracking-wider vscode-text-tertiary mb-3">Contact</h4>
                    <a href={`mailto:${c.contact_email}`} className="flex items-center gap-2 text-sm text-blue-400 hover:text-blue-300">
                      <Mail className="w-4 h-4" />
                      {c.contact_email}
                    </a>
                  </div>
                )}

                <RecordMetaCard
                  title="Details"
                  rows={[
                    { label: 'Created', value: formatDateTimeGmt(c.created_at) },
                    { label: 'Masking', value: c.default_masking_template_id ? maskingNames[c.default_masking_template_id] ?? 'Custom' : 'Default' },
                  ]}
                />
              </div>
            </ListPreviewDrawer>
          );
        })()
      ) : null}

      {clientRowMenu ? (
        <div
          ref={clientRowMenuRef}
          className="fixed z-[160] min-w-[200px] rounded-lg border vscode-border vscode-bg-elevated py-1 shadow-xl"
          style={{ left: clientRowMenu.x, top: clientRowMenu.y }}
          role="menu"
        >
          <button
            type="button"
            className="block w-full px-3 py-2 text-left text-sm vscode-text-primary hover:vscode-bg-hover"
            onClick={() => {
              setPreviewClientId(clientRowMenu.clientId);
              setClientRowMenu(null);
            }}
          >
            Open preview
          </button>
          <button
            type="button"
            className="block w-full px-3 py-2 text-left text-sm vscode-text-primary hover:vscode-bg-hover"
            onClick={() => {
              navigate(`/clients/${clientRowMenu.clientId}`);
              setClientRowMenu(null);
            }}
          >
            Open in full page
          </button>
        </div>
      ) : null}
    </div>
  );
}
